"use client"

import {
  File06,
  Headphones01,
  Image01,
  MusicNote01,
  PlayCircle,
} from "@untitledui/icons"
import { useEffect, useRef, useState } from "react"

import { RichText } from "@/components/ui/rich-text"
import { type BridgeController, findBridge } from "@/lib/player/bridges"
import { coordinator } from "@/lib/player/coordinator"
import type { PlayableItem, PlayableKind } from "@/lib/player/playable-item"
import { cn, focalPointStyle } from "@/lib/utils"

export type ContentPlayerMode = "live" | "poster"
export type ContentPlayerAspect = "9/16" | "16/9" | "1/1" | "4/5" | "4/3"

export interface ContentPlayerProps {
  item: PlayableItem
  mode: ContentPlayerMode
  /**
   * Whether this instance currently holds the global active slot. Only
   * meaningful when mode === "live" for video/audio — iframes only autoplay
   * while active. Shells (immersive feed, inline preview) decide who is
   * active based on scroll/click.
   */
  active?: boolean
  /**
   * Global mute state. The iframe reloads when this changes (the embed URL
   * carries the mute flag as a query param) — a limping-by-design limitation
   * until a real provider bridge can drive mute without remounting. Defaults
   * to `true` so that autoplay is allowed by browser policy when a shell
   * forgets to thread the coordinator's `isMuted` through.
   */
  muted?: boolean
  /** Invoked when the poster is activated (click/Enter). */
  onRequestLive?: () => void
  /**
   * If set, overrides the default aspect ratio for the slot. Immersive feed
   * uses 9/16; inline preview uses 16/9 for video, 1/1 for audio.
   */
  aspect?: ContentPlayerAspect
  className?: string
}

const ASPECT_CLASS: Record<ContentPlayerAspect, string> = {
  "9/16": "aspect-[9/16]",
  "16/9": "aspect-video",
  "1/1": "aspect-square",
  "4/5": "aspect-[4/5]",
  "4/3": "aspect-[4/3]",
}

const DEFAULT_ASPECT: Record<PlayableKind, ContentPlayerAspect> = {
  video: "16/9",
  audio: "1/1",
  photo: "4/5",
  article: "4/5",
}

/**
 * Resolves the slot's aspect ratio:
 *   1. an explicit shell override (immersive feed always wants 9/16)
 *   2. content-level intent (`item.aspect` from VideoBody.aspect_label)
 *   3. per-kind default
 *
 * The narrowing guard rejects unknown values defensively so a future
 * backend enum addition that the frontend doesn't yet handle falls
 * through to the default instead of producing `aspect-[bogus]`.
 */
function resolveAspect(
  item: PlayableItem,
  override?: ContentPlayerAspect,
): ContentPlayerAspect {
  if (override) return override
  if (item.aspect && item.aspect in ASPECT_CLASS) {
    return item.aspect
  }
  return DEFAULT_ASPECT[item.kind]
}

const KIND_ICON = {
  video: PlayCircle,
  audio: MusicNote01,
  photo: Image01,
  article: File06,
} as const

/**
 * The one primitive every player surface renders. Takes a PlayableItem and a
 * mode; renders the correct thing. Doesn't know anything about feeds, modals,
 * routes, or keyboards — that's the shell's job.
 */
export function ContentPlayer({
  item,
  mode,
  active = false,
  muted = true,
  onRequestLive,
  aspect,
  className,
}: ContentPlayerProps) {
  const resolvedAspect = resolveAspect(item, aspect)

  return (
    // @container enables the chip below to self-scale via `cqi` units so
    // the play affordance looks proportional at any poster size (tiny brief
    // thumbnails → full-viewport immersive feed) without each shell having
    // to hand in a chip-size class.
    <div
      className={cn(
        "@container relative w-full overflow-hidden rounded-xl",
        ASPECT_CLASS[resolvedAspect],
        className,
      )}
    >
      {mode === "poster" ? (
        <Poster item={item} onActivate={onRequestLive} />
      ) : (
        <Live item={item} active={active} muted={muted} />
      )}
    </div>
  )
}

/* ------------------------------------------------------------------ poster */

function Poster({
  item,
  onActivate,
}: {
  item: PlayableItem
  onActivate?: () => void
}) {
  const Icon = KIND_ICON[item.kind]
  const hasPoster = !!item.poster?.src

  return (
    <button
      type="button"
      onClick={onActivate}
      aria-label={`Play ${item.title}`}
      className="group relative block h-full w-full cursor-pointer"
    >
      {hasPoster ? (
        <img
          src={item.poster!.src}
          alt=""
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
          style={focalPointStyle(item.poster?.focal_point)}
        />
      ) : (
        <div className="flex h-full w-full items-center justify-center bg-gradient-to-br from-neutral-100 to-neutral-300">
          <Icon className="size-10 text-neutral-500" />
        </div>
      )}

      {/* Subtle gradient wash + play chip so the affordance is unmistakable
          without the video-lightbox-style dark overlay we're moving away from. */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent opacity-80 transition-opacity group-hover:opacity-100" />
      {item.kind !== "photo" && (
        <div className="pointer-events-none absolute inset-0 flex items-center justify-center">
          <div className="flex size-[clamp(44px,18cqi,72px)] items-center justify-center rounded-full bg-white/95 text-neutral-900 shadow-lg ring-1 ring-black/5 backdrop-blur-sm transition-transform duration-200 group-hover:scale-105">
            {item.kind === "audio" ? (
              <Headphones01 className="h-1/2 w-1/2" />
            ) : item.kind === "article" ? (
              <File06 className="h-[45%] w-[45%]" />
            ) : (
              <PlayCircle className="h-[55%] w-[55%]" />
            )}
          </div>
        </div>
      )}
    </button>
  )
}

/* -------------------------------------------------------------------- live */

function Live({
  item,
  active,
  muted,
}: {
  item: PlayableItem
  active: boolean
  muted: boolean
}) {
  switch (item.kind) {
    case "video":
    case "audio":
      return <LiveIframe item={item} active={active} muted={muted} />
    case "photo":
      return <LivePhoto item={item} />
    case "article":
      return <LiveArticle item={item} />
    default:
      return null
  }
}

function LiveIframe({
  item,
  active,
  muted,
}: {
  item: PlayableItem
  active: boolean
  muted: boolean
}) {
  const iframeRef = useRef<HTMLIFrameElement | null>(null)
  const controllerRef = useRef<BridgeController | null>(null)

  // Chrome shield. YouTube summons its overlay UI (title/share header,
  // control bar) on any pointer activity inside the iframe — including a
  // cursor merely resting over the stage — and none of it can be disabled
  // by URL params anymore (`showinfo`/`modestbranding` are retired). For
  // bridged embeds we don't need the iframe to see the pointer at all, so
  // a transparent shield keeps the provider blind and the video clean. A
  // deliberate click hands events back (native chrome becomes reachable);
  // leaving the player re-arms the shield. Keyboard focus tabs straight
  // into the iframe underneath, so keyboard users always get full native
  // controls.
  const [chromeRevealed, setChromeRevealed] = useState(false)
  const shielded = !!item.embed_url && !!findBridge(item.embed_url)

  // Provider bridge attach. When a matching bridge (today: YouTube) exists,
  // it owns play/pause/mute over postMessage to the existing iframe — no
  // src reload on mute toggle. The duration timer below is still the
  // fallback end-detection for providers without a bridge.
  useEffect(() => {
    if (!item.embed_url || !iframeRef.current) return
    const bridge = findBridge(item.embed_url)
    if (!bridge) return

    const controller = bridge.attach(iframeRef.current)
    controllerRef.current = controller
    const unsubscribe = controller.subscribeEnded(() => {
      coordinator.emitEnded(item.id)
    })
    return () => {
      unsubscribe()
      controller.destroy()
      controllerRef.current = null
    }
  }, [item.embed_url, item.id])

  // Mute toggle without remount. Falls through harmlessly when there's no
  // bridge for this embed — the iframe src still carries the mute query
  // param as the autoplay-policy fallback.
  useEffect(() => {
    controllerRef.current?.setMuted(muted)
  }, [muted])

  // Duration-based fallback end-detection. Real provider APIs (YouTube
  // IFrame API, Spotify Embed API) can hook end-of-track precisely and
  // emit through the bridge above — both paths funnel through
  // coordinator.emitEnded so shells only subscribe in one place.
  useEffect(() => {
    if (!active) return
    const ms = (item.duration_seconds ?? 0) * 1000
    if (ms <= 0) return
    const handle = window.setTimeout(() => {
      coordinator.emitEnded(item.id)
    }, ms)
    return () => window.clearTimeout(handle)
  }, [active, item.duration_seconds, item.id])

  // Pause happens by virtue of the shell flipping mode="live" → "poster"
  // (or unmounting the slide), which detaches the iframe. A bridged
  // provider would also call pause/setMuted directly on its embed —
  // unmount is the universal fallback.

  if (!item.embed_url) {
    return <FallbackLink sourceUrl={item.source_url} title={item.title} />
  }

  const src = buildIframeSrc(item.embed_url, { autoplay: active, muted })

  return (
    <div
      className="relative h-full w-full"
      onPointerLeave={() => setChromeRevealed(false)}
    >
      <iframe
        ref={iframeRef}
        src={src}
        title={item.title}
        className="h-full w-full border-0"
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
        allowFullScreen
        loading="lazy"
      />
      {shielded && !chromeRevealed && (
        <div
          aria-hidden="true"
          className="absolute inset-0"
          onPointerUp={(e) => {
            // Mirror the native click-on-video gesture so the swallowed
            // first click isn't dead: mouse click pauses (as YouTube's own
            // body-click does); touch just reveals (as YouTube's first tap
            // does). Either way the native chrome is reachable next.
            if (e.pointerType !== "touch") controllerRef.current?.pause()
            setChromeRevealed(true)
          }}
        />
      )}
    </div>
  )
}

function LivePhoto({ item }: { item: PlayableItem }) {
  useEffect(() => {
    const handle = window.setTimeout(() => {
      coordinator.emitEnded(item.id)
    }, 6000)
    return () => window.clearTimeout(handle)
  }, [item.id])
  return (
    <img
      src={item.poster?.src}
      alt={item.title}
      className="h-full w-full object-cover"
      style={focalPointStyle(item.poster?.focal_point)}
    />
  )
}

function LiveArticle({ item }: { item: PlayableItem }) {
  const body = item.body
  return (
    <div
      data-article-slide
      data-item-id={item.id}
      className="flex h-full flex-col bg-white px-4 py-3 sm:px-5 sm:py-4"
    >
      <header className="mb-2 shrink-0 border-b border-black/5 pb-2">
        {item.byline && (
          <p className="text-[10px] font-semibold tracking-wide text-gray-500 uppercase">
            {`Article · ${item.byline}`}
          </p>
        )}
        {item.title && (
          <h2 className="mt-1 line-clamp-2 text-base leading-snug font-semibold text-gray-900">
            {item.title}
          </h2>
        )}
      </header>

      <div className="relative -mx-1 flex-1 overflow-y-auto px-1">
        {body ? (
          <div className="prose prose-sm prose-p:my-2 prose-p:leading-relaxed prose-headings:text-gray-900 prose-a:text-blue-600 hover:prose-a:text-blue-700 prose-strong:text-gray-900 max-w-none text-gray-800">
            <RichText content={body} />
            {item.source_url && (
              <p className="mt-3 text-xs">
                <a
                  href={item.source_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 font-medium text-blue-600 hover:text-blue-700"
                >
                  Read full article →
                </a>
              </p>
            )}
          </div>
        ) : item.source_url ? (
          <a
            href={item.source_url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-sm text-blue-600 underline"
          >
            Read on source
          </a>
        ) : null}
      </div>
    </div>
  )
}

function FallbackLink({
  sourceUrl,
  title,
}: {
  sourceUrl?: string
  title: string
}) {
  return (
    <div className="flex h-full w-full items-center justify-center bg-neutral-100 p-6 text-center text-sm text-neutral-700">
      <div>
        <p className="font-medium">{title}</p>
        {sourceUrl && (
          <a
            href={sourceUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 inline-block text-blue-600 underline"
          >
            Open source
          </a>
        )}
      </div>
    </div>
  )
}

/**
 * Append the provider-agnostic autoplay + mute hints to an embed URL. Real
 * providers may have their own names for these params (YouTube uses `mute`,
 * Vimeo uses `muted`) — keeping both covers the two providers most third-
 * party embeds go through, and the rest ignore unknown params.
 */
function buildIframeSrc(
  embedUrl: string,
  { autoplay, muted }: { autoplay: boolean; muted: boolean },
): string {
  try {
    const url = new URL(embedUrl)
    url.searchParams.set("autoplay", autoplay ? "1" : "0")
    url.searchParams.set("mute", muted ? "1" : "0")
    url.searchParams.set("muted", muted ? "1" : "0")
    url.searchParams.set("playsinline", "1")
    return url.toString()
  } catch {
    return embedUrl
  }
}
